import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { workflowApi } from '../../services/api';
import WorkflowPipeline from './WorkflowPipeline';
import {
  Search,
  Users,
  Eye,
  ChevronLeft,
  ChevronRight,
  Loader2,
  UserCircle,
  Building2,
  MessageSquare,
  AlertCircle,
  Check
} from 'lucide-react';
import type { Task, Customer } from '../../types';

const WorkflowPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedTask, setSelectedTask] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  // Initialize selectedTask from URL parameter
  useEffect(() => {
    const stateParam = searchParams.get('state');
    if (stateParam) {
      setSelectedTask(parseInt(stateParam));
    }
  }, [searchParams]);

  // Fetch all workflow tasks
  const { data: tasks, isLoading: tasksLoading, refetch: refetchTasks } = useQuery({
    queryKey: ['workflow-tasks'],
    queryFn: async () => {
      const response = await workflowApi.getTasks();
      return response.data.data || response.data;
    },
  });

  // Fetch customers for selected task
  const {
    data: customersData,
    isLoading: customersLoading,
    refetch: refetchCustomers
  } = useQuery({
    queryKey: ['workflow-customers', selectedTask, searchQuery, page],
    queryFn: async () => {
      if (!selectedTask) return null;
      const response = await workflowApi.getTaskCustomers(selectedTask, {
        search: searchQuery || undefined,
        page,
        per_page: 10,
      });
      return response.data;
    },
    enabled: !!selectedTask,
  });

  const handleTaskSelect = (taskNumber: number) => {
    setSelectedTask(taskNumber);
    setPage(1);
    setSearchQuery('');
  };

  const handleCustomerClick = (customer: Customer) => {
    // Navigate to full page customer details
    navigate(`/workflow/customers/${customer.uuid}?fromState=${selectedTask}`);
  };

  // Sort customers: interventions first, then responses
  const sortedCustomers = customersData?.data ? [...customersData.data].sort((a: Customer, b: Customer) => {
    const aHasClosedIntervention = a.state_before_intervention && !a.intervention_conseiller;
    const bHasClosedIntervention = b.state_before_intervention && !b.intervention_conseiller;
    const aHasOpenIntervention = a.intervention_conseiller;
    const bHasOpenIntervention = b.intervention_conseiller;
    // Only prioritize unprocessed responses (not yet validated)
    const aHasUnprocessedResponse = (a.reponse_suppletive && !a.suppletive_flow_processed) || (a.reponse_centrale && !a.centrale_flow_processed);
    const bHasUnprocessedResponse = (b.reponse_suppletive && !b.suppletive_flow_processed) || (b.reponse_centrale && !b.centrale_flow_processed);

    // Priority: Closed intervention > Open intervention > Unprocessed Response > Normal
    if (aHasClosedIntervention && !bHasClosedIntervention) return -1;
    if (!aHasClosedIntervention && bHasClosedIntervention) return 1;
    if (aHasOpenIntervention && !bHasOpenIntervention) return -1;
    if (!aHasOpenIntervention && bHasOpenIntervention) return 1;
    if (aHasUnprocessedResponse && !bHasUnprocessedResponse) return -1;
    if (!aHasUnprocessedResponse && bHasUnprocessedResponse) return 1;
    return 0;
  }) : [];

  const selectedTaskData = tasks?.find((t: Task) => t.task_number === selectedTask);

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Workflow</h1>
        <p className="text-gray-600 mt-1">Gestion du parcours client et des transitions d'état</p>
      </div>

      {/* Pipeline */}
      {tasksLoading ? (
        <div className="bg-white rounded-lg shadow-lg p-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : tasks ? (
        <WorkflowPipeline
          tasks={tasks}
          selectedTask={selectedTask}
          onSelectTask={handleTaskSelect}
        />
      ) : null}

      {/* Customer List */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-gray-500" />
              <div>
                <h2 className="font-semibold text-gray-900">
                  {selectedTaskData?.name || 'Sélectionnez un état'}
                </h2>
                {selectedTaskData && (
                  <p className="text-sm text-gray-500">
                    {customersData?.meta?.total || 0} clients dans cet état
                  </p>
                )}
              </div>
            </div>

            {/* Search */}
            {selectedTask && (
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un client..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                />
              </div>
            )}
          </div>
        </div>

        {/* Customer List Content */}
        <div className="min-h-[400px]">
          {!selectedTask ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <p className="font-medium">Sélectionnez un état dans le pipeline</p>
              <p className="text-sm mt-1">pour voir les clients correspondants</p>
            </div>
          ) : customersLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : customersData?.data && customersData.data.length > 0 ? (
            <>
              {/* Customer Cards */}
              <div className="divide-y divide-gray-100">
                {sortedCustomers.map((customer: Customer) => {
                  const hasReponseSuppletive = !!customer.reponse_suppletive;
                  const hasReponseCentrale = !!customer.reponse_centrale;
                  // Only highlight if response exists AND is not yet processed
                  const hasUnprocessedSuppletive = hasReponseSuppletive && !customer.suppletive_flow_processed;
                  const hasUnprocessedCentrale = hasReponseCentrale && !customer.centrale_flow_processed;
                  const hasAnyUnprocessedResponse = hasUnprocessedSuppletive || hasUnprocessedCentrale;
                  const hasAnyResponse = hasReponseSuppletive || hasReponseCentrale;
                  const hasOpenIntervention = !!customer.intervention_conseiller;
                  const hasClosedIntervention = !!customer.state_before_intervention && !customer.intervention_conseiller;

                  return (
                    <div
                      key={customer.id}
                      className={`p-4 transition-colors cursor-pointer group relative ${
                        hasOpenIntervention
                          ? 'bg-gradient-to-r from-red-50 to-transparent hover:from-red-100 border-l-3 border-red-500'
                          : hasClosedIntervention
                          ? 'bg-gradient-to-r from-purple-50 to-transparent hover:from-purple-100 border-l-3 border-purple-500'
                          : hasAnyUnprocessedResponse
                          ? 'bg-gradient-to-r from-amber-50 to-transparent hover:from-amber-100'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => handleCustomerClick(customer)}
                    >
                      {/* Intervention Indicators */}
                      {hasOpenIntervention && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-red-500 to-red-400" />
                      )}
                      {hasClosedIntervention && !hasOpenIntervention && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-600 to-purple-500" />
                      )}
                      {/* New Response Indicator - only show for unprocessed responses */}
                      {hasAnyUnprocessedResponse && !hasOpenIntervention && !hasClosedIntervention && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-500 to-orange-500" />
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 min-w-0">
                          {/* Avatar */}
                          <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold ${
                            hasOpenIntervention
                              ? 'bg-gradient-to-br from-red-400 to-red-600 ring-2 ring-red-200'
                              : hasClosedIntervention
                              ? 'bg-gradient-to-br from-purple-400 to-purple-600 ring-2 ring-purple-200'
                              : hasAnyUnprocessedResponse
                              ? 'bg-gradient-to-br from-amber-400 to-orange-600 ring-2 ring-amber-300'
                              : 'bg-gradient-to-br from-primary-400 to-primary-600'
                          }`}>
                            {customer.full_name?.charAt(0)?.toUpperCase() || <UserCircle className="w-6 h-6" />}
                          </div>

                          {/* Info */}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-gray-900 truncate">
                                {customer.full_name}
                              </h3>
                              {/* Intervention Status Badges in Name Area */}
                              {hasOpenIntervention && (
                                <span className="flex-shrink-0 px-2.5 py-1 text-xs font-semibold rounded-full bg-red-500 text-white animate-pulse flex items-center gap-1">
                                  <MessageSquare className="w-3.5 h-3.5" />
                                  Intervention
                                </span>
                              )}
                              {hasClosedIntervention && (
                                <span className="flex-shrink-0 px-2.5 py-1 text-xs font-semibold rounded-full bg-purple-600 text-white flex items-center gap-1">
                                  <AlertCircle className="w-3.5 h-3.5" />
                                  À vérifier
                                </span>
                              )}
                              {hasAnyUnprocessedResponse && !hasOpenIntervention && !hasClosedIntervention && (
                                <span className="flex-shrink-0 px-2 py-0.5 text-xs font-bold rounded-full bg-amber-500 text-white animate-pulse">
                                  NOUVEAU
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-500 mt-0.5">
                              <span className="truncate">{customer.login}</span>
                              {customer.company_name && (
                                <span className="flex items-center gap-1 truncate">
                                  <Building2 className="w-3 h-3" />
                                  {customer.company_name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right Side */}
                        <div className="flex items-center gap-2">
                          {/* Intervention Badges */}
                          {customer.intervention_conseiller && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md bg-red-100 text-red-800 border border-red-300">
                              <MessageSquare className="w-4 h-4" />
                              Intervention ouverte
                            </span>
                          )}
                          {customer.state_before_intervention && !customer.intervention_conseiller && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md bg-purple-100 text-purple-800 border border-purple-300">
                              <AlertCircle className="w-4 h-4" />
                              À vérifier
                            </span>
                          )}

                          {/* Response Type Badges - Only show in State 3 */}
                          {customer.state === 3 && hasReponseSuppletive && (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-md border ${
                              customer.suppletive_flow_processed
                                ? 'bg-green-100 text-green-800 border-green-300'
                                : 'bg-amber-100 text-amber-800 border-amber-300'
                            }`}>
                              {customer.suppletive_flow_processed ? <Check className="w-3 h-3" /> : null}
                              📄 Supplétive
                            </span>
                          )}
                          {customer.state === 3 && hasReponseCentrale && (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-md border ${
                              customer.centrale_flow_processed
                                ? 'bg-green-100 text-green-800 border-green-300'
                                : 'bg-blue-100 text-blue-800 border-blue-300'
                            }`}>
                              {customer.centrale_flow_processed ? <Check className="w-3 h-3" /> : null}
                              🏛️ Centrale
                            </span>
                          )}

                          {/* Sub-status Badge */}
                          {customer.subStatus && !hasAnyResponse && !customer.intervention_conseiller && !customer.state_before_intervention && (
                            <span className="hidden md:inline-flex px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                              {customer.subStatus}
                            </span>
                          )}

                          {/* View Button */}
                          <button
                            className={`p-2 rounded-lg transition-colors ${
                              hasOpenIntervention
                                ? 'text-red-600 hover:text-red-700 hover:bg-red-50'
                                : hasClosedIntervention
                                ? 'text-purple-600 hover:text-purple-700 hover:bg-purple-50'
                                : hasAnyResponse
                                ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-100'
                                : 'text-gray-400 hover:text-primary-600 hover:bg-primary-50 group-hover:text-primary-600'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCustomerClick(customer);
                            }}
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {customersData.meta && customersData.meta.last_page > 1 && (
                <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Affichage {customersData.meta.from} - {customersData.meta.to} sur {customersData.meta.total}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-gray-700 px-3">
                      Page {page} / {customersData.meta.last_page}
                    </span>
                    <button
                      onClick={() => setPage(p => Math.min(customersData.meta.last_page, p + 1))}
                      disabled={page === customersData.meta.last_page}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <p className="font-medium">Aucun client trouvé</p>
              <p className="text-sm mt-1">
                {searchQuery ? 'Essayez une autre recherche' : 'Aucun client dans cet état'}
              </p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default WorkflowPage;
